/**
 * @module useEmailValidation
 * @description Custom hook for validating email addresses
 */

import { useState } from 'react';
import emailValidator from '../tools/emailValidator';

/**
 * @function useEmailValidation
 * @description Provides email validation state and functionality
 *
 * @returns {Object} - An object containing validation state and methods:
 *   - {Boolean} isInvalid - Whether the email is currently marked as invalid
 *   - {Function} setIsInvalid - Setter function to manually update invalid state
 *   - {Function} validateEmail - Function to validate an email and update state
 *
 * @example
 * const { isInvalid, validateEmail } = useEmailValidation();
 *
 * // In your JSX:
 * <input
 *   type="email"
 *   value={email}
 *   onChange={(e) => setEmail(e.target.value)}
 *   onBlur={() => validateEmail(email)}
 *   className={isInvalid ? 'invalid' : ''}
 * />
 * {isInvalid && <span className="error">Please enter a valid email</span>}
 */
export default function useEmailValidation() {
  const [isInvalid, setIsInvalid] = useState(false);

  /**
   * @function validateEmail
   * @description Validates an email address and updates the invalid state
   * @param {String} email - The email address to validate
   * @returns {Boolean} - Whether the email is valid
   */
  const validateEmail = (email) => {
    const isValid = emailValidator(email);
    setIsInvalid(!isValid);
    return isValid;
  };

  return { isInvalid, setIsInvalid, validateEmail };
}
